import { Component, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BdeoDrawer } from '@bdeo/webcomponents';
import { SmartphotoService } from '../../../../services/smartphoto.service';
import { Smartphoto } from '@bdeo-motor/smartphoto-entities';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs';

@Component({
  selector: 'app-smartphoto-damage-estimation-price-drawer',
  templateUrl: './smartphoto-damage-estimation-price-drawer.component.html',
  styleUrl: './smartphoto-damage-estimation-price-drawer.component.scss'
})
export class SmartphotoDamageEstimationPriceDrawerComponent {
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly smartphotoChanged$ = this.smartphotoService.getCurrentSmartphoto().pipe(
    filter((s): s is Smartphoto => Boolean(s)),
    tap((smartphoto) => {
      this.smartphoto = smartphoto;
      this.currency = smartphoto.estimationConfig.currency;
      this.resetForm();
    })
  );
  @ViewChild('drawer') private drawer!: { nativeElement: BdeoDrawer };
  public smartphoto?: Smartphoto;
  public paintHourPrice = new FormControl<number>(0, [Validators.required, Validators.min(0)]);
  public labourHourPrice = new FormControl<number>(0, [Validators.required, Validators.min(0)]);
  public priceForm = new FormGroup({
    paintHourPrice: this.paintHourPrice,
    labourHourPrice: this.labourHourPrice
  });
  public currency = '€';

  constructor() {
    this.smartphotoChanged$.pipe(takeUntilDestroyed()).subscribe();
  }

  private resetForm(): void {
    this.paintHourPrice.setValue(this.smartphoto?.estimationConfig.paintHourPrice ?? 0);
    this.labourHourPrice.setValue(this.smartphoto?.estimationConfig.labourHourPrice ?? 0);
    this.priceForm.markAsPristine();
  }

  public open(): void {
    this.drawer.nativeElement.show = true;
  }

  public close(): void {
    this.drawer.nativeElement.show = false;
    this.resetForm();
  }

  public async save(): Promise<void> {
    const { labourHourPrice, paintHourPrice } = this.priceForm.getRawValue();

    if (!labourHourPrice || !paintHourPrice || !this.smartphoto || !this.smartphoto.estimation) {
      return;
    }
    this.smartphoto.estimationConfig.labourHourPrice = labourHourPrice;
    this.smartphoto.estimationConfig.paintHourPrice = paintHourPrice;

    const damages = [...this.smartphoto.getMainDamages(), ...this.smartphoto.concepts];

    damages.forEach((damage) => {
      damage.estimation.updatePrice({
        labour: labourHourPrice,
        paint: paintHourPrice
      });
    });

    this.smartphoto.estimation.calculate(damages);
    this.smartphotoService.updateSmartphoto(this.smartphoto);
    this.smartphotoService.createUpdateTransaction();

    this.close();
  }

  public increment(formControl: FormControl<number | null>, incrementAmount = 1): void {
    formControl.setValue(Math.round((Number(formControl.value ?? 0) + incrementAmount) * 100) / 100);

    formControl.markAsDirty();
  }

  public decrement(formControl: FormControl<number | null>, decrementAmount = 1): void {
    if (!formControl.value || formControl.value - decrementAmount < 0) {
      formControl.setValue(0);
    } else {
      formControl.setValue(Math.round((Number(formControl.value) - decrementAmount) * 100) / 100);
    }
    formControl.markAsDirty();
  }
}
